import { useEffect, useState } from "react";
import { api, type Customer, type FollowUp, type Lead, type Project } from "./api.js";

const LEAD_STAGES = ["NEW", "CONTACTED", "QUALIFIED", "SITE_ASSESSMENT", "PROPOSAL", "NEGOTIATION", "WON", "LOST"];
const NEXT_STAGE: Record<string, string> = {
  NEW: "CONTACTED", CONTACTED: "QUALIFIED", QUALIFIED: "SITE_ASSESSMENT",
  SITE_ASSESSMENT: "PROPOSAL", PROPOSAL: "NEGOTIATION", NEGOTIATION: "WON",
};
const STATUS_CHIP: Record<string, string> = {
  FEASIBILITY: "info", DESIGN: "warn", PROPOSAL_SENT: "info", ACCEPTED: "ok",
  OPERATING: "ok", COMPLETED: "ok", CANCELLED: "bad",
};

export default function Dashboard({ onOpenProject }: { onOpenProject: (id: string, name: string) => void }): JSX.Element {
  const [tab, setTab] = useState<"projects" | "pipeline" | "widget" | "prices" | "integrations">("projects");
  const [projects, setProjects] = useState<Project[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [board, setBoard] = useState<{ leads: Record<string, Lead[]>; projects: Record<string, Project[]> } | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState("");

  async function reload(): Promise<void> {
    try {
      const [p, c, b] = await Promise.all([api.projects.list(), api.customers.list(), api.pipeline()]);
      setProjects(p);
      setCustomers(c);
      setBoard(b);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void reload();
    api.org().then((o) => setWidgetKey(o.widgetKey)).catch(() => undefined);
  }, []);

  const filtered = projects.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const custName = (id?: string | null) => customers.find((c) => c.id === id)?.lastName ?? "—";

  return (
    <div className="dash">
      <aside className="dash-side">
        <div className={`ds-item ${tab === "projects" ? "active" : ""}`} onClick={() => setTab("projects")}>
          <div>📋 Projects<span>All PV projects of your organization</span></div>
        </div>
        <div className={`ds-item ${tab === "pipeline" ? "active" : ""}`} onClick={() => setTab("pipeline")}>
          <div>📊 Pipeline<span>Leads & projects by stage — your sales board</span></div>
        </div>
        <div className={`ds-item ${tab === "widget" ? "active" : ""}`} onClick={() => setTab("widget")}>
          <div>🧲 Lead widget<span>White-label calculator for your website</span></div>
        </div>
        <div className={`ds-item ${tab === "prices" ? "active" : ""}`} onClick={() => setTab("prices")}>
          <div>💶 Price book<span>Your purchase prices, labor rates & margin</span></div>
        </div>
        <div className={`ds-item ${tab === "integrations" ? "active" : ""}`} onClick={() => setTab("integrations")}>
          <div>🔌 Integrations<span>Data sources, providers & connections</span></div>
        </div>
      </aside>
      <main className="dash-main">
        {error && <div className="msg err">{error}</div>}
        {tab === "integrations" ? (
          <IntegrationsTab />
        ) : tab === "prices" ? (
          <PriceBookTab />
        ) : tab === "widget" ? (
          <WidgetTab widgetKey={widgetKey} />
        ) : tab === "projects" ? (
          <>
            <h1>My projects</h1>
            <div className="kpis">
              <div className="kpi"><div className="l">Projects</div><div className="v">{projects.length}</div></div>
              <div className="kpi"><div className="l">Customers</div><div className="v">{customers.length}</div></div>
              <div className="kpi"><div className="l">Open leads</div><div className="v">{board ? Object.entries(board.leads).filter(([s]) => s !== "WON" && s !== "LOST").reduce((n, [, l]) => n + l.length, 0) : "–"}</div></div>
              <div className="kpi"><div className="l">Accepted / operating</div><div className="v">{projects.filter((p) => ["ACCEPTED", "OPERATING", "COMPLETED"].includes(p.status)).length}</div></div>
            </div>
            <div className="toolbar">
              <button className="btn btn-primary" onClick={() => setShowNew(true)}>Add project</button>
              <input type="text" placeholder="🔍 Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>Name</th><th>Customer</th><th>Type</th><th>Status</th><th>Updated</th></tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="row" onClick={() => onOpenProject(p.id, p.name)}>
                      <td><b>{p.name}</b></td>
                      <td>{custName(p.customerId)}</td>
                      <td className="muted">{p.projectType.replaceAll("_", " ").toLowerCase()}</td>
                      <td><span className={`chip ${STATUS_CHIP[p.status] ?? ""}`}>{p.status.replaceAll("_", " ")}</span></td>
                      <td className="muted">{new Date(p.updatedAt).toLocaleDateString("de-DE")}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 26 }}>☰ No projects yet — click “Add project”.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <h1>Sales pipeline</h1>
            <div className="muted" style={{ margin: "4px 0 16px" }}>Advance leads stage by stage; won leads become projects.</div>
            <div className="board">
              {LEAD_STAGES.map((stage) => (
                <div className="board-col" key={stage}>
                  <h4>{stage.replaceAll("_", " ")}<span>{board?.leads[stage]?.length ?? 0}</span></h4>
                  {(board?.leads[stage] ?? []).map((l) => (
                    <div className="board-card" key={l.id} onClick={() => setOpenLead(l.id)}>
                      <b>{custName(l.customerId)}</b>
                      <span className="muted">
                        {l.estimatedKwp ? `${l.estimatedKwp} kWp · ` : ""}
                        {l.source === "WEB_CALCULATOR" ? "🧲 widget" : l.source.toLowerCase()}
                      </span>
                      <div style={{ marginTop: 6, display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {NEXT_STAGE[stage] && (
                          <button className="btn btn-sm" onClick={() => void api.leads.update(l.id, { stage: NEXT_STAGE[stage]! }).then(reload)}>
                            → {NEXT_STAGE[stage]!.replaceAll("_", " ").toLowerCase()}
                          </button>
                        )}
                        {stage !== "LOST" && stage !== "WON" && (
                          <button className="btn btn-sm" onClick={() => void api.leads.update(l.id, { stage: "LOST" }).then(reload)}>✕</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
        {showNew && <NewProjectModal customers={customers} onClose={() => setShowNew(false)} onCreated={(id, name) => { setShowNew(false); onOpenProject(id, name); }} />}
        {openLead && (
          <LeadDrawer
            leadId={openLead}
            onClose={() => { setOpenLead(null); void reload(); }}
            onProjectCreated={(id, name) => onOpenProject(id, name)}
          />
        )}
      </main>
    </div>
  );
}

/* ---------------------------- Lead detail drawer -------------------------- */

function LeadDrawer({ leadId, onClose, onProjectCreated }: {
  leadId: string;
  onClose: () => void;
  onProjectCreated: (id: string, name: string) => void;
}): JSX.Element {
  const [data, setData] = useState<{ lead: Lead; customer: Customer | null; followUps: FollowUp[] } | null>(null);
  const [dueAt, setDueAt] = useState(() => new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10));
  const [kind, setKind] = useState("call");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.leads.detail(leadId).then(setData).catch((e) => setErr(String(e)));
  useEffect(() => { void load(); }, [leadId]);

  async function addFollowUp(): Promise<void> {
    await api.leads.addFollowUp(leadId, { dueAt: new Date(dueAt).toISOString(), kind, note: note || undefined });
    setNote("");
    await load();
  }

  async function convert(): Promise<void> {
    if (!data) return;
    const p = await api.projects.create({
      name: `PV ${data.customer?.lastName ?? "project"}`,
      customerId: data.lead.customerId,
      leadId: data.lead.id,
    });
    onProjectCreated(p.id, p.name);
  }

  const wp = data?.lead.widgetPayload as { roofAreaM2?: number; annualConsumptionKwh?: number; batteryInterest?: boolean; estimate?: { kwp: number; savingsEurPerYear: number; paybackYears: number } } | null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 380, maxWidth: "92vw", borderRadius: "12px 0 0 12px", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        {err && <div className="msg err">{err}</div>}
        {!data ? <span className="spinner" /> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h2>{data.customer?.lastName ?? "Lead"}</h2>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {data.customer?.email ?? "no email"} · {data.customer?.phone ?? "no phone"}
                </div>
              </div>
              <button className="btn btn-sm" onClick={onClose}>✕</button>
            </div>
            <div style={{ margin: "10px 0" }}>
              <span className="chip info">{data.lead.stage.replaceAll("_", " ")}</span>{" "}
              <span className="chip">{data.lead.source === "WEB_CALCULATOR" ? "🧲 widget lead" : data.lead.source.toLowerCase()}</span>
            </div>
            {wp?.estimate && (
              <div className="msg info">
                Widget estimate: <b>{wp.estimate.kwp} kWp</b> · savings ≈ {Math.round(wp.estimate.savingsEurPerYear)} €/yr · payback ≈ {wp.estimate.paybackYears} yr
                {wp.roofAreaM2 ? ` · roof ~${wp.roofAreaM2} m²` : ""}{wp.batteryInterest ? " · wants battery" : ""}
              </div>
            )}
            {data.lead.notes && <div className="msg">{data.lead.notes}</div>}
            <h3 style={{ margin: "14px 0 8px" }}>Follow-ups</h3>
            {data.followUps.length === 0 && <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>None yet.</div>}
            {data.followUps.map((f) => (
              <div key={f.id} className={`check ${f.doneAt ? "donec" : ""}`}>
                <div className="cb" onClick={() => void api.leads.setFollowUpDone(leadId, f.id, !f.doneAt).then(load)}>
                  {f.doneAt ? "✓" : ""}
                </div>
                <div className="t">
                  <b>{f.kind} · {new Date(f.dueAt).toLocaleDateString("de-DE")}</b>
                  <span>{f.note ?? ""}{!f.doneAt && new Date(f.dueAt) < new Date() ? " · ⚠ overdue" : ""}</span>
                </div>
              </div>
            ))}
            <div className="frow" style={{ marginTop: 8 }}>
              <div className="field"><label>Due</label><input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} /></div>
              <div className="field"><label>Kind</label>
                <select value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="call">Call</option><option value="email">Email</option>
                  <option value="visit">Site visit</option><option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="field" style={{ marginTop: 8 }}><label>Note</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to do…" /></div>
            <button className="btn btn-o" style={{ marginTop: 8, width: "100%" }} onClick={() => void addFollowUp()}>＋ Add follow-up</button>
            <button className="btn btn-primary" style={{ marginTop: 14, width: "100%" }} onClick={() => void convert()}>
              ⚡ Create project from this lead
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Price book tab ---------------------------- */

const RATE_ROWS: { key: string; label: string; defaultEur: number; unit: string }[] = [
  { key: "labor.residential", label: "Installation labor — residential", defaultEur: 300, unit: "€/kWp" },
  { key: "labor.commercial", label: "Installation labor — commercial", defaultEur: 260, unit: "€/kWp" },
  { key: "electrical.residential", label: "Electrical BOS — residential", defaultEur: 200, unit: "€/kWp" },
  { key: "electrical.commercial", label: "Electrical BOS — commercial", defaultEur: 170, unit: "€/kWp" },
  { key: "mounting.pitched_roof", label: "Mounting — pitched roof", defaultEur: 130, unit: "€/kWp" },
  { key: "mounting.flat_roof", label: "Mounting — flat roof (ballast)", defaultEur: 110, unit: "€/kWp" },
  { key: "mounting.ground", label: "Mounting — ground frames", defaultEur: 85, unit: "€/kWp" },
  { key: "laborPerModule", label: "Labor per module", defaultEur: 12, unit: "€/module" },
  { key: "scaffolding", label: "Scaffolding (pitched roof, fixed)", defaultEur: 900, unit: "€" },
  { key: "batteryInstall", label: "Battery installation (fixed)", defaultEur: 800, unit: "€" },
  { key: "planning.residential", label: "Planning & documentation — residential", defaultEur: 900, unit: "€" },
  { key: "margin.pct", label: "Company margin", defaultEur: 0, unit: "%" },
  { key: "regional.factor", label: "Regional cost factor", defaultEur: 1, unit: "×" },
];

function PriceBookTab(): JSX.Element {
  const [entries, setEntries] = useState<Record<string, number>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [components, setComponents] = useState<{ kind: string; id: string; label: string; listPriceEur: number }[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.priceBook.get().then((r) => setEntries(Object.fromEntries(r.entries.map((e) => [e.key, e.valueEur]))));
    void api.components().then((c) =>
      setComponents([
        ...c.modules.map((m) => ({ kind: "module", id: m.id, label: `${m.manufacturer} ${m.model}`, listPriceEur: m.listPriceEur })),
        ...c.inverters.map((i) => ({ kind: "inverter", id: i.id, label: `${i.manufacturer} ${i.model}`, listPriceEur: i.listPriceEur })),
        ...c.batteries.map((b) => ({ kind: "battery", id: b.id, label: `${b.manufacturer} ${b.model}`, listPriceEur: b.listPriceEur })),
      ]),
    );
  }, []);

  function setDraft(key: string, v: string): void {
    setDrafts((d) => ({ ...d, [key]: v }));
  }

  async function save(): Promise<void> {
    const changed = Object.entries(drafts).map(([key, v]) => ({
      key,
      valueEur: v.trim() === "" ? null : Number(v.replace(",", ".")),
    })).filter((e) => e.valueEur === null || Number.isFinite(e.valueEur));
    if (changed.length === 0) return;
    setBusy(true);
    setNote("");
    try {
      const r = await api.priceBook.put(changed);
      setEntries(Object.fromEntries(r.entries.map((e) => [e.key, e.valueEur])));
      setDrafts({});
      setNote("✓ Saved — every new simulation and proposal now uses your prices.");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const cell = (key: string, fallback: number | "") => (
    <input
      type="text"
      style={{ width: 110, textAlign: "right" }}
      placeholder={fallback === "" ? "" : String(fallback)}
      value={drafts[key] ?? (entries[key] !== undefined ? String(entries[key]) : "")}
      onChange={(e) => setDraft(key, e.target.value)}
    />
  );

  return (
    <div>
      <h1>Price book</h1>
      <div className="muted" style={{ margin: "4px 0 16px", maxWidth: 720 }}>
        Overwrite any price: your component purchase/sales prices, labor and mounting rates, margin.
        Empty field = platform default (shown greyed). Changes apply to every new simulation, cost estimate and proposal — existing signed proposals are untouched.
      </div>
      {note && <div className={`msg ${note.startsWith("Error") ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ maxWidth: 760, marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Rates & margin</h3>
        <table className="tbl">
          <tbody>
            {RATE_ROWS.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="muted">{r.unit}</td>
                <td className="r">{cell(r.key, r.defaultEur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ maxWidth: 760, marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Component prices (your EK/VK instead of list price)</h3>
        <table className="tbl">
          <thead><tr><th>Component</th><th className="r">List €</th><th className="r">Your price €</th></tr></thead>
          <tbody>
            {components.map((c) => (
              <tr key={`${c.kind}:${c.id}`}>
                <td>{c.label} <span className="muted" style={{ fontSize: 11 }}>({c.kind})</span></td>
                <td className="r muted">{c.listPriceEur.toLocaleString("de-DE")}</td>
                <td className="r">{cell(`${c.kind}:${c.id}`, "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn btn-primary" disabled={busy || Object.keys(drafts).length === 0} onClick={() => void save()}>
        {busy ? <span className="spinner" /> : `Save ${Object.keys(drafts).length || ""} change${Object.keys(drafts).length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}

/* ---------------------------- Widget embed tab ---------------------------- */

function WidgetTab({ widgetKey }: { widgetKey: string }): JSX.Element {
  const apiOrigin = window.location.origin;
  const embed = `<div id="pv-widget" data-key="${widgetKey || "…"}" data-api="${apiOrigin}"></div>\n<script src="${apiOrigin}/pv-widget.js" defer></script>`;
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <h1>Lead-capture widget</h1>
      <div className="muted" style={{ margin: "4px 0 16px" }}>
        Paste this snippet into your website. Visitors get an instant solar estimate; their request lands in your Pipeline as a lead — with roof size, consumption and the shown estimate attached.
      </div>
      <div className="card" style={{ maxWidth: 720 }}>
        <h3 style={{ marginBottom: 8 }}>Your embed code</h3>
        <pre style={{ background: "var(--code-bg)", padding: 12, borderRadius: 8, fontSize: 12, overflowX: "auto" }}>{embed}</pre>
        <button
          className="btn btn-o btn-sm"
          onClick={() => { void navigator.clipboard.writeText(embed).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
        >
          {copied ? "✓ Copied" : "⧉ Copy snippet"}
        </button>
        <div className="msg info" style={{ marginTop: 12 }}>
          Widget key: <b>{widgetKey || "loading…"}</b> — keep it public-safe: it can only create leads in your organization (rate-limited, spam-trapped).
        </div>
        <div className="msg info">
          Demo page: open <code>apps/widget/demo.html</code> after building the widget (<code>npm run build</code> in <code>apps/widget</code>).
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ customers, onClose, onCreated }: {
  customers: Customer[];
  onClose: () => void;
  onCreated: (id: string, name: string) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState("");
  const [projectType, setProjectType] = useState("RESIDENTIAL_PITCHED");
  const [withLead, setWithLead] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(): Promise<void> {
    setError("");
    setBusy(true);
    try {
      let cid = customerId;
      if (!cid && newCustomer.trim()) {
        const c = await api.customers.create({ lastName: newCustomer.trim() });
        cid = c.id;
      }
      let leadId: string | undefined;
      if (withLead && cid) {
        const lead = await api.leads.create({ customerId: cid, source: "MANUAL" });
        leadId = lead.id;
      }
      const p = await api.projects.create({ name: name || "New project", customerId: cid || undefined, leadId, projectType });
      onCreated(p.id, p.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--scrim-strong)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: 480, maxWidth: "94vw" }}>
        <h2 style={{ marginBottom: 14 }}>New PV project</h2>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>Project name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. EFH Schmidt" />
        </div>
        <div className="frow" style={{ marginBottom: 10 }}>
          <div className="field">
            <label>Customer</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">— new customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.lastName}{c.firstName ? `, ${c.firstName}` : ""}</option>
              ))}
            </select>
          </div>
          {!customerId && (
            <div className="field">
              <label>New customer name</label>
              <input value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} placeholder="Last name / company" />
            </div>
          )}
        </div>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>Project type</label>
          <select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
            <option value="RESIDENTIAL_PITCHED">Residential — pitched roof</option>
            <option value="RESIDENTIAL_FLAT">Residential — flat roof</option>
            <option value="COMMERCIAL_FLAT">Commercial — flat roof</option>
            <option value="INDUSTRIAL">Industrial rooftop</option>
            <option value="GROUND_MOUNTED">Ground-mounted</option>
            <option value="CARPORT">Carport</option>
          </select>
        </div>
        <label style={{ fontSize: 12.5, display: "flex", gap: 7, alignItems: "center", marginBottom: 12 }}>
          <input type="checkbox" checked={withLead} onChange={(e) => setWithLead(e.target.checked)} style={{ width: "auto" }} />
          Also create a lead in the sales pipeline
        </label>
        {error && <div className="msg err">{error}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void create()}>
            {busy ? <span className="spinner" /> : "Create project →"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------- Integrations (Data Sources) ------------------- */

function IntegrationsTab(): JSX.Element {
  const [data, setData] = useState<{ view: "member" | "admin"; plan: string; providers: import("./api.js").IntegrationProvider[] } | null>(null);
  const [note, setNote] = useState("");
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({});

  const load = () => api.integrations.list().then(setData).catch((e) => setNote(String(e)));
  useEffect(() => { void load(); }, []);

  if (!data) return <div style={{ padding: 30 }}><span className="spinner" /></div>;
  const isAdmin = data.view === "admin";
  const badge = (p: import("./api.js").IntegrationProvider) =>
    p.status === "active" ? <span className="chip ok">active</span>
    : p.status === "stub" ? <span className="chip">coming</span>
    : <span className="chip info">locked</span>;

  return (
    <>
      <h1>Data sources & integrations</h1>
      <div className="sub">
        The platform always picks the best sufficient source automatically — free and open data first. Paid providers are only ever used after explicit approval. Plan: <b>{data.plan}</b>
      </div>
      {note && <div className="msg err">{note}</div>}
      <table className="tbl">
        <thead><tr><th>Provider</th><th>Type</th><th className="r">Confidence</th><th className="r">Cost</th><th>Status</th>{isAdmin && <th>Configuration</th>}</tr></thead>
        <tbody>
          {data.providers.map((p) => (
            <tr key={p.id}>
              <td>
                <b>{p.name}</b>
                <br /><span className="muted" style={{ fontSize: 10.5 }}>{p.freshness}{p.lockReason ? ` · ${p.lockReason}` : ""}</span>
              </td>
              <td><span className="chip">{p.type.replace("_", " ")}</span></td>
              <td className="r">{p.confidencePct > 0 ? `${p.confidencePct} %` : "—"}</td>
              <td className="r">{p.costLabel}</td>
              <td>{badge(p)}</td>
              {isAdmin && (
                <td style={{ whiteSpace: "nowrap" }}>
                  <label style={{ fontSize: 11.5, marginRight: 8 }}>
                    <input
                      type="checkbox"
                      checked={p.settings?.enabled ?? p.status === "active"}
                      onChange={(e) => {
                        void api.integrations.update(p.id, { enabled: e.target.checked }).then(load);
                      }}
                    /> enabled
                  </label>
                  {p.auth === "api_key" && (
                    <>
                      <input
                        type="password"
                        placeholder={p.settings?.apiKeySet ? "key set ✓ (replace…)" : "API key…"}
                        value={keyDraft[p.id] ?? ""}
                        onChange={(e) => setKeyDraft({ ...keyDraft, [p.id]: e.target.value })}
                        style={{ width: 130, padding: "3px 6px", fontSize: 11.5 }}
                      />{" "}
                      <button
                        className="btn btn-sm"
                        disabled={!keyDraft[p.id]}
                        onClick={() => {
                          void api.integrations.update(p.id, { apiKey: keyDraft[p.id]!, enabled: true }).then(() => {
                            setKeyDraft({ ...keyDraft, [p.id]: "" });
                            void load();
                          });
                        }}
                      >Save</button>
                    </>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
        Locked providers are gated by plan or provider contracts — they are shown so you know what is possible, and never run without approval. Every provider selection and paid-data approval is recorded in the project's engineering decision history.
      </div>
    </>
  );
}
